import React, { useState, useRef, useEffect } from "react";
import { useParams } from "react-router-dom";

import {
  User,
  FileText,
  PenTool,
  CheckCircle,
  AlertCircle,
  Upload,
} from "lucide-react";

const Visitor = () => {
  const { visitId } = useParams();
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    company: "",
    purpose: "",
    licensePlate: "",
    meetingLocation: "",
  });
  const [visitMeta, setVisitMeta] = useState({
    isDriving: false,
    requiresDocument: false,
    documentType: null,
    requiresDocumentApproval: false,
    isApprovalRequired: false,
  });
  const [agreementAccepted, setAgreementAccepted] = useState(false);
  const [signatureData, setSignatureData] = useState(null);
  const [currentStep, setCurrentStep] = useState(1);
  const [isDrawing, setIsDrawing] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  // Document upload state
  const [documentFile, setDocumentFile] = useState(null);
  const [documentUploaded, setDocumentUploaded] = useState(false);
  const [isUploadingDoc, setIsUploadingDoc] = useState(false);
  const [docUploadError, setDocUploadError] = useState("");

  // Warning modal state (replaces browser alert())
  const [warnModal, setWarnModal] = useState({ show: false, message: "" });
  const showWarn = (message) => setWarnModal({ show: true, message });

  const canvasRef = useRef(null);
  const isDrawingRef = useRef(false);
  // Always-current ref so handleSubmit never closes over a stale formData
  const latestFormData = useRef(formData);

  // Step helpers: if requiresDocument → steps 1,2,3,4 else 1,3,4 (skip doc step)
  const getNextStep = (step) => {
    if (step === 1) return visitMeta.requiresDocument ? 2 : 3;
    if (step === 2) return 3;
    if (step === 3) return 4;
    return step;
  };

  const getPrevStep = (step) => {
    if (step === 2) return 1;
    if (step === 3) return visitMeta.requiresDocument ? 2 : 1;
    if (step === 4) return 3;
    return step;
  };

  const totalSteps = visitMeta.requiresDocument ? 4 : 3;
  const displayStep = visitMeta.requiresDocument
    ? currentStep
    : currentStep === 3 ? 2 : currentStep === 4 ? 3 : currentStep;

  // Fetch visitor data on component mount
  useEffect(() => {
    const fetchVisitorData = async () => {
      if (!visitId) {
        console.log("No visitId found");
        return;
      }

      console.log("Fetching data for visitId:", visitId);

      try {
        setIsLoading(true);
        const response = await fetch(
          `https://vms-backend-dot-vms-demo-495410.el.r.appspot.com/api/visits/${visitId}`
        );

        console.log("Response status:", response.status);

        if (response.ok) {
          const visitData = await response.json();
          console.log("Fetched visit data:", visitData);

          const fetchedFormData = {
            name: visitData.visitor?.Name || "",
            email: visitData.visitor?.email || "",
            company: visitData.visitor?.company || "",
            purpose: visitData.purpose || "",
            licensePlate: visitData.licensePlate || "",
            meetingLocation: visitData.meetingLocation || "",
          };
          setFormData(fetchedFormData);
          latestFormData.current = fetchedFormData;

          setVisitMeta({
            isDriving: visitData.isDriving === true,
            requiresDocument: visitData.requiresDocument === true,
            documentType: visitData.documentType || null,
            requiresDocumentApproval: visitData.requiresDocumentApproval === true,
            isApprovalRequired: visitData.isApprovalRequired === true,
          });

          console.log("Form data set");
        } else {
          console.error("Failed to fetch visitor data:", response.status);
        }
      } catch (error) {
        console.error("Error fetching visitor data:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchVisitorData();
  }, [visitId]);

  // Initialize canvas
  useEffect(() => {
    const canvas = canvasRef.current;
    if (canvas) {
      const ctx = canvas.getContext("2d");
      ctx.strokeStyle = "#000";
      ctx.lineWidth = 2;
      ctx.lineCap = "round";
    }
  }, [currentStep]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    latestFormData.current = { ...latestFormData.current, [name]: value };
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  // Signature canvas functions
  const startDrawing = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const ctx = canvas.getContext("2d");

    isDrawingRef.current = true;
    setIsDrawing(true);

    ctx.beginPath();
    ctx.moveTo(e.clientX - rect.left, e.clientY - rect.top);
  };

  const draw = (e) => {
    if (!isDrawingRef.current) return;

    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const ctx = canvas.getContext("2d");

    ctx.lineTo(e.clientX - rect.left, e.clientY - rect.top);
    ctx.stroke();
  };

  const stopDrawing = () => {
    isDrawingRef.current = false;
    setIsDrawing(false);

    const canvas = canvasRef.current;
    setSignatureData(canvas.toDataURL());
  };

  const clearSignature = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setSignatureData(null);
  };

  // Document upload handler
  const handleDocumentUpload = async () => {
    if (!documentFile) {
      setDocUploadError("Please select a file to upload.");
      return;
    }

    setIsUploadingDoc(true);
    setDocUploadError("");

    try {
      const fd = new FormData();
      fd.append("document", documentFile);

      const response = await fetch(
        `https://vms-backend-dot-vms-demo-495410.el.r.appspot.com/api/visits/${visitId}/upload-document`,
        {
          method: "POST",
          body: fd,
        }
      );

      if (response.ok) {
        setDocumentUploaded(true);
      } else {
        const errText = await response.text();
        setDocUploadError("Upload failed: " + errText);
      }
    } catch (error) {
      console.error("Error uploading document:", error);
      setDocUploadError("Upload failed. Please try again.");
    } finally {
      setIsUploadingDoc(false);
    }
  };

  const handleSubmit = async () => {
    if (!agreementAccepted) {
      showWarn("Please accept the NDA agreement to continue.");
      return;
    }

    if (!signatureData) {
      showWarn("Please provide your signature to complete the registration.");
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      const fd = latestFormData.current;

      const payload = {
        signature: signatureData,
        Name: fd.name,
        company: fd.company,
        purpose: fd.purpose,
        meetingLocation: fd.meetingLocation,
      };

      if (visitMeta.isDriving && fd.licensePlate) {
        payload.licensePlate = fd.licensePlate;
      }

      console.log("Submitting to visitId:", visitId);

      const signatureResponse = await fetch(
        `https://vms-backend-dot-vms-demo-495410.el.r.appspot.com/api/visits/${visitId}/complete`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        }
      );

      console.log("Response status:", signatureResponse.status);

      if (signatureResponse.ok) {
        const result = await signatureResponse.json();
        console.log("Success response:", result);
        setSubmitStatus("success");
      } else {
        const errorText = await signatureResponse.text();
        console.error("Error response:", errorText);
        setSubmitStatus("error");
      }
    } catch (error) {
      console.error("Error submitting form:", error);
      setSubmitStatus("error");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleNext = () => {
    if (currentStep === 1) {
      if (!formData.name) {
        showWarn("Please enter your full name.");
        return;
      }
      if (visitMeta.isDriving && !formData.licensePlate) {
        showWarn("Please enter your license plate number.");
        return;
      }
    }
    if (currentStep === 2) {
      if (!documentUploaded) {
        showWarn(`Please upload your ${documentTypeLabel} to continue.`);
        return;
      }
    }
    if (currentStep === 3 && !agreementAccepted) {
      showWarn("Please accept the NDA agreement to continue.");
      return;
    }
    setCurrentStep(getNextStep(currentStep));
  };

  const documentTypeLabel =
    visitMeta.documentType === "passport"
      ? "Passport"
      : visitMeta.documentType === "permanent_ic"
      ? "Permanent Identity Card"
      : visitMeta.documentType || "Document";

  if (isLoading) {
    return (
      <div className="max-w-2xl mx-auto p-6 text-center">
        <p>Loading visitor information...</p>
      </div>
    );
  }

  if (!visitId) {
    return (
      <div className="max-w-2xl mx-auto p-6 text-center">
        <p className="text-red-600">
          Invalid visit link. Please check your email for the correct link.
        </p>
      </div>
    );
  }

  if (submitStatus === "success") {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-100">
        <div className="max-w-2xl p-6 bg-white rounded-lg shadow-lg">
          <div className="text-center">
            {visitMeta.requiresDocumentApproval ? (
              <>
                <div className="w-16 h-16 rounded-full bg-yellow-100 flex items-center justify-center mx-auto mb-4">
                  <svg className="w-8 h-8 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <h2 className="text-2xl font-bold text-gray-800 mb-2">
                  Submission Received
                </h2>
                <p className="text-gray-600 mb-2">
                  Your pre-registration and document have been submitted successfully.
                </p>
                <p className="text-sm text-yellow-700 bg-yellow-50 border border-yellow-200 rounded-lg px-4 py-3 inline-block">
                  Your document is under review by the admin. You will receive a confirmation email with your QR code once it has been approved.
                </p>
              </>
            ) : visitMeta.isApprovalRequired ? (
              <>
                <CheckCircle className="w-16 h-16 text-green-600 mx-auto mb-4" />
                <h2 className="text-2xl font-bold text-gray-800 mb-2">
                  Registration Complete!
                </h2>
                <p className="text-gray-600">
                  Pre-Registration Complete! You will receive a confirmation email with your QR code once approved.
                </p>
              </>
            ) : (
              <>
                <CheckCircle className="w-16 h-16 text-green-600 mx-auto mb-4" />
                <h2 className="text-2xl font-bold text-gray-800 mb-2">
                  Registration Complete!
                </h2>
                <p className="text-gray-600">
                  You will receive a confirmation email shortly.
                </p>
              </>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-2xl lg:max-w-4xl mx-auto p-6">

      {/* Warning modal — replaces browser alert() */}
      {warnModal.show && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl p-6 w-full max-w-sm mx-4">
            <div className="flex items-start gap-3 mb-4">
              <div className="w-9 h-9 rounded-full bg-yellow-100 flex items-center justify-center flex-shrink-0">
                <svg className="w-5 h-5 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
                </svg>
              </div>
              <p className="text-sm text-gray-700 mt-1">{warnModal.message}</p>
            </div>
            <button
              onClick={() => setWarnModal({ show: false, message: "" })}
              className="w-full px-4 py-2 bg-blue-800 text-white text-sm font-medium rounded-lg hover:bg-blue-900 transition-colors"
            >
              OK
            </button>
          </div>
        </div>
      )}

      <div className="text-center mb-8">
        <h1 className="text-3xl font-bold text-gray-800 mb-2">
          Visitor Pre-Registration
        </h1>
        <p className="text-gray-600">
          Please complete all sections to register for your visit
        </p>
        {/* Step indicator */}
        <div className="flex justify-center gap-2 mt-4">
          {Array.from({ length: totalSteps }, (_, i) => (
            <div
              key={i}
              className={`w-2.5 h-2.5 rounded-full ${
                displayStep > i + 1
                  ? "bg-blue-800"
                  : displayStep === i + 1
                  ? "bg-blue-800"
                  : "bg-gray-300"
              }`}
            />
          ))}
        </div>
        <p className="text-xs text-gray-500 mt-2">
          Step {displayStep} of {totalSteps}
        </p>
      </div>

      <div className="bg-white rounded-lg shadow-lg">
        {/* Step 1: Personal Information */}
        {currentStep === 1 && (
          <div className="space-y-6">
            <div className="bg-gray-50 p-6 rounded-lg">
              <div className="flex items-center mb-4">
                <User className="w-5 h-5 text-blue-800 mr-2" />
                <h2 className="text-xl font-semibold text-gray-800">
                  Personal Information
                </h2>
              </div>

              <div className="bg-blue-50 border-l-4 border-blue-400 p-4 mb-6">
                <div className="flex items-start">
                  <AlertCircle className="w-5 h-5 text-blue-800 mt-0.5 mr-2 flex-shrink-0" />
                  <div>
                    <p className="text-blue-800 font-medium">
                      Important Verification Notice
                    </p>
                    <p className="text-blue-800 text-sm mt-1">
                      Please ensure your full name exactly matches that on your
                      government-issued ID. You may edit your name and company
                      below if needed.
                    </p>
                  </div>
                </div>
              </div>

              <div className="max-w-2xl mx-auto space-y-4">
                {/* Full Name - editable */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Full Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Enter your full name"
                    required
                  />
                </div>

                {/* Email - read-only */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Email Address
                  </label>
                  <div className="w-full px-3 py-2 border border-gray-200 rounded-md bg-gray-50 text-sm text-gray-600 select-none">
                    {formData.email || "-"}
                  </div>
                </div>

                {/* Company - editable */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Company / Organization
                  </label>
                  <input
                    type="text"
                    name="company"
                    value={formData.company}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                {/* Purpose - read-only */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Purpose of Visit
                  </label>
                  <div className="w-full px-3 py-2 border border-gray-200 rounded-md bg-gray-50 text-sm text-gray-600 select-none">
                    {formData.purpose || "-"}
                  </div>
                </div>

                {/* Meeting Location - read-only */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Meeting Location
                  </label>
                  <div className="w-full px-3 py-2 border border-gray-200 rounded-md bg-gray-50 text-sm text-gray-600 select-none">
                    {formData.meetingLocation || "-"}
                  </div>
                </div>

                {/* License Plate - editable, saves to vehicle-info on blur */}
                {visitMeta.isDriving && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      License Plate Number <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="licensePlate"
                      value={formData.licensePlate}
                      onChange={handleInputChange}
                      onBlur={async (e) => {
                        const val = e.target.value.trim();
                        if (!val || !visitId) return;
                        try {
                          await fetch(
                            `https://vms-backend-dot-vms-demo-495410.el.r.appspot.com/api/visits/${visitId}/vehicle-info`,
                            {
                              method: "PUT",
                              headers: { "Content-Type": "application/json" },
                              body: JSON.stringify({ licensePlate: val }),
                            }
                          );
                        } catch (err) {
                          console.error("Failed to save license plate:", err);
                        }
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Enter your vehicle license plate"
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Step 2: Document Upload (only if requiresDocument) */}
        {currentStep === 2 && visitMeta.requiresDocument && (
          <div className="space-y-6">
            <div className="bg-gray-50 p-6 rounded-lg">
              <div className="flex items-center mb-4">
                <Upload className="w-5 h-5 text-blue-800 mr-2" />
                <h2 className="text-xl font-semibold text-gray-800">
                  Document Upload
                </h2>
              </div>

              <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-6">
                <div className="flex items-start">
                  <AlertCircle className="w-5 h-5 text-yellow-600 mt-0.5 mr-2 flex-shrink-0" />
                  <p className="text-yellow-800 text-sm">
                    Your host has requested that you submit your{" "}
                    <strong>{documentTypeLabel}</strong> before your visit. Please
                    upload a clear copy below.
                  </p>
                </div>
              </div>

              <div className="max-w-2xl mx-auto">
                {documentUploaded ? (
                  <div className="flex items-center gap-3 p-4 bg-green-50 border border-green-200 rounded-lg">
                    <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0" />
                    <div>
                      <p className="text-green-800 font-medium">Document uploaded successfully!</p>
                      <p className="text-green-600 text-sm">{documentFile?.name}</p>
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        setDocumentUploaded(false);
                        setDocumentFile(null);
                      }}
                      className="ml-auto text-sm text-gray-500 hover:text-gray-700 underline"
                    >
                      Replace
                    </button>
                  </div>
                ) : (
                  <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
                    <Upload className="w-10 h-10 text-gray-400 mx-auto mb-3" />
                    <p className="text-sm text-gray-600 mb-4">
                      Upload your <strong>{documentTypeLabel}</strong>
                    </p>
                    <input
                      type="file"
                      accept="image/*,.pdf"
                      onChange={(e) => {
                        setDocumentFile(e.target.files[0]);
                        setDocUploadError("");
                      }}
                      className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-800 hover:file:bg-blue-100 mb-4"
                    />
                    {documentFile && !documentUploaded && (
                      <div className="mt-2">
                        <p className="text-xs text-gray-500 mb-3">
                          Selected: {documentFile.name}
                        </p>
                        <button
                          type="button"
                          onClick={handleDocumentUpload}
                          disabled={isUploadingDoc}
                          className={`px-6 py-2 rounded-md text-white font-medium transition-colors ${
                            isUploadingDoc
                              ? "bg-gray-400 cursor-not-allowed"
                              : "bg-blue-800 hover:bg-blue-800"
                          }`}
                        >
                          {isUploadingDoc ? "Uploading..." : "Upload Document"}
                        </button>
                      </div>
                    )}
                    {docUploadError && (
                      <p className="text-red-600 text-sm mt-2">{docUploadError}</p>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Step 3: NDA Agreement */}
        {currentStep === 3 && (
          <div className="space-y-6">
            <div className="bg-gray-50 p-6 rounded-lg">
              <div className="flex items-center mb-4">
                <FileText className="w-5 h-5 text-blue-800 mr-2" />
                <h2 className="text-xl font-semibold text-gray-800">
                  Non-Disclosure Agreement
                </h2>
              </div>

              <div className="bg-white border rounded-lg p-4 max-h-72 overflow-y-auto mb-4">
                <div className="text-sm text-gray-700 space-y-3">
                  <p className="font-semibold">
                    CONFIDENTIALITY AND NON-DISCLOSURE AGREEMENT
                  </p>

                  <p>
                    By signing this agreement, you acknowledge and agree to the
                    following terms and conditions regarding your visit to our
                    premises:
                  </p>

                  <ol className="list-decimal list-inside space-y-2 ml-4">
                    <li>
                      <strong>Confidential Information:</strong> You may be
                      exposed to confidential, proprietary, or sensitive
                      information during your visit. This includes but is not
                      limited to business strategies, technical data, financial
                      information, and personnel matters.
                    </li>
                    <li>
                      <strong>Non-Disclosure:</strong> You agree not to
                      disclose, share, or use any confidential information
                      obtained during your visit for any purpose other than the
                      intended business purpose.
                    </li>
                    <li>
                      <strong>Security Compliance:</strong> You agree to follow
                      all security protocols and guidelines provided during your
                      visit, including badge requirements and restricted area
                      policies.
                    </li>
                    <li>
                      <strong>Compliance:</strong> You agree to comply with all
                      company policies and applicable laws during your visit.
                    </li>
                  </ol>

                  <p>
                    This agreement remains in effect indefinitely and survives
                    the termination of your visit. Violation of this agreement
                    may result in legal action.
                  </p>
                </div>
              </div>

              <div className="flex items-start mb-6">
                <input
                  type="checkbox"
                  id="nda-agreement"
                  checked={agreementAccepted}
                  onChange={(e) => setAgreementAccepted(e.target.checked)}
                  className="mt-1 mr-3 w-4 h-4 text-blue-800 border-gray-300 rounded focus:ring-blue-500"
                />
                <label htmlFor="nda-agreement" className="text-sm text-gray-700">
                  I have read, understood, and agree to the terms of this
                  Non-Disclosure Agreement. E-signing this agreement confirms my
                  acceptance and commitment to comply with all stated terms.
                </label>
              </div>
            </div>
          </div>
        )}

        {/* Step 4: E-Signature */}
        {currentStep === 4 && (
          <div className="space-y-6">
            <div className="bg-gray-50 p-6 rounded-lg">
              <div className="flex items-center mb-4">
                <PenTool className="w-5 h-5 text-blue-800 mr-2" />
                <h2 className="text-xl font-semibold text-gray-800">
                  Electronic Signature
                </h2>
              </div>

              <p className="text-sm text-gray-600 mb-4">
                Please sign below to complete your registration. Your signature
                will be securely stored.
              </p>

              <div className="border-2 border-gray-300 border-dashed rounded-lg p-4 bg-white max-w-2xl mx-auto">
                <canvas
                  ref={canvasRef}
                  width={500}
                  height={200}
                  className="w-full h-48 border border-gray-200 rounded cursor-crosshair"
                  onMouseDown={startDrawing}
                  onMouseMove={draw}
                  onMouseUp={stopDrawing}
                  onMouseLeave={stopDrawing}
                />

                <div className="flex justify-between items-center mt-3">
                  <p className="text-xs text-gray-500">
                    Sign above using your mouse or touch device
                  </p>
                  <button
                    type="button"
                    onClick={clearSignature}
                    className="text-sm text-blue-800 hover:text-blue-800 underline"
                  >
                    Clear Signature
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Navigation Buttons */}
      <div className="flex justify-between items-center mt-8">
        {currentStep > 1 && (
          <button
            type="button"
            onClick={() => setCurrentStep(getPrevStep(currentStep))}
            className="px-6 py-3 bg-gray-600 text-white rounded-md hover:bg-gray-700 transition-colors"
          >
            Previous
          </button>
        )}

        <div className="flex-1"></div>

        {currentStep < 4 ? (
          <button
            type="button"
            onClick={handleNext}
            className="px-6 py-3 bg-blue-800 text-white rounded-md hover:bg-blue-800 transition-colors"
          >
            Next
          </button>
        ) : (
          <button
            type="button"
            onClick={handleSubmit}
            disabled={isSubmitting || !signatureData}
            className={`px-8 py-3 rounded-md font-medium transition-colors ${
              isSubmitting || !signatureData
                ? "bg-gray-400 cursor-not-allowed"
                : "bg-blue-800 hover:bg-blue-800"
            } text-white`}
          >
            {isSubmitting ? "Submitting..." : "Complete Registration"}
          </button>
        )}
      </div>

      {submitStatus === "error" && (
        <p className="text-red-600 text-sm mt-2 text-center">
          An error occurred. Please try again.
        </p>
      )}
    </div>
  );
};

export default Visitor;
